// Apolo IDE — useBrainMCP hook (Sprint v2 Epic B6 WS B6.4).
//
// Thin wrapper around window.apolo.mcp.call() with a 60s in-memory
// cache keyed by (serverId, toolName, args-json). The cache is module-
// level so it survives tab re-mounts but resets on hot reload / refresh.
//
// Returns { data, loading, error, refetch }. Calls are deduped: a
// second invocation with the same key while the first is in-flight
// reuses the pending promise.

import { useCallback, useEffect, useRef, useState } from 'react';

const TTL_MS = 60_000;
const cache = new Map();   // key -> { value, expiresAt }
const inflight = new Map();// key -> Promise

function keyFor(serverId, toolName, args) {
  return `${serverId}::${toolName}::${JSON.stringify(args || {})}`;
}

function getBridge() {
  if (typeof window === 'undefined') return null;
  return window.apolo?.mcp || null;
}

export function clearBrainMCPCache() {
  cache.clear();
  inflight.clear();
}

export async function callBrainMCP({ serverId = 'obsidian', toolName, args = {}, bypassCache = false }) {
  const bridge = getBridge();
  if (!bridge) throw new Error('MCP bridge unavailable (renderer not in Electron)');
  if (!toolName) throw new Error('toolName required');
  const key = keyFor(serverId, toolName, args);
  if (!bypassCache) {
    const hit = cache.get(key);
    if (hit && hit.expiresAt > Date.now()) return hit.value;
    if (inflight.has(key)) return inflight.get(key);
  }
  const p = (async () => {
    try {
      const value = await bridge.call(serverId, toolName, args);
      cache.set(key, { value, expiresAt: Date.now() + TTL_MS });
      return value;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export function useBrainMCP(toolName, args, { serverId = 'obsidian', enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const argsRef = useRef(args);
  argsRef.current = args;

  const fetcher = useCallback(async (bypassCache = false) => {
    if (!toolName) return;
    setLoading(true);
    setError(null);
    try {
      const value = await callBrainMCP({
        serverId,
        toolName,
        args: argsRef.current,
        bypassCache,
      });
      setData(value);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [serverId, toolName]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetcher(false);
    return undefined;
  }, [enabled, fetcher, JSON.stringify(args || {})]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    loading,
    error,
    refetch: useCallback(() => fetcher(true), [fetcher]),
  };
}
